import { Component, OnInit, OnDestroy } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { CustomerService } from "src/app/services/customer/customer.service";
import { Customer } from "../../models/customer.model";
import { CustomerFormData } from "../../models/customer-form-data.model";
import { CustomerFormSubmitEvent } from "../shared/customer-form/customer-form.component";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-customer-details",
  templateUrl: "./customer-details.component.html",
  styleUrls: ["./customer-details.component.scss"],
})
export class CustomerDetailsComponent implements OnInit, OnDestroy {
  isLoading = false;
  isSaving = false;
  editMode = false;
  customerId: number | null = null;
  customerData: Customer | null = null;
  
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private customerService: CustomerService
  ) {}

  ngOnInit() {
    this.initializeComponent();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeComponent(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.customerId = params['id'] ? parseInt(params['id'], 10) : null;
        this.editMode = !!this.customerId;
        
        if (this.editMode && this.customerId) {
          this.loadCustomerData(this.customerId);
        }
      });
  }

  private loadCustomerData(customerId: number): void {
    this.isLoading = true;
    
    this.customerService.getCompleteCustomerData(customerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (completeData) => {
          this.customerData = this.transformCompleteDataToFormData(completeData);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading customer:', error);
          this.toastr.error('Failed to load customer data', 'Error');
          this.isLoading = false;
          this.router.navigate(['/customers']);
        }
      });
  }

  private transformCompleteDataToFormData(completeData: any): any {
    const adminUsers = completeData.users
      .filter((user: any) => user.roles.some((role: any) => role.name.toLowerCase() === 'admin'))
      .map((user: any) => ({
        username: user.username,
        password: '', 
        userDetails: user.userDetails,
        regionNames: user.regions.map((region: any) => region.name),
        siteNames: user.sites.map((site: any) => site.name)
      }));

    const operatorUsers = completeData.users
      .filter((user: any) => user.roles.some((role: any) => role.name.toLowerCase() === 'user'))
      .map((user: any) => ({
        username: user.username,
        password: '', 
        userDetails: user.userDetails,
        regionNames: user.regions.map((region: any) => region.name),
        siteNames: user.sites.map((site: any) => site.name)
      }));

    return {
      customer: completeData.customer,
      customerProfile: completeData.customerProfile,
      regions: completeData.regions,
      sites: completeData.sites,
      adminUsers: adminUsers,
      operatorUsers: operatorUsers
    };
  }

  onFormSubmit(event: CustomerFormSubmitEvent): void {
    if (!event.isValid) {
      this.toastr.warning('Please complete all required fields', 'Validation Error');
      return;
    }

    this.isSaving = true;
    const customerData = event.customerData;

    if (this.editMode && this.customerId) {
      this.updateCustomer(this.customerId, customerData);
    } else {
      this.createCustomer(customerData);
    }
  }

  private createCustomer(formData: CustomerFormData): void {
    const customerPayload = this.transformFormDataToAPI(formData);
    
    this.customerService.createCustomerComplete(customerPayload)
      .pipe(takeUntil(this.destroy$))  
      .subscribe({
        next: (response) => {
          this.isSaving = false;
          this.toastr.success('Customer created successfully!', 'Success');
          this.router.navigate(['/customers']);
        },
        error: (error) => {
          this.isSaving = false;
          console.error('Error creating customer:', error);
          if(error && error.error && error.error.message) {
            this.toastr.error(error.error.message, 'Error');
            return;
          }
          this.toastr.error('Failed to create customer', 'Error');
        }
      });
  }

  private updateCustomer(customerId: number, formData: CustomerFormData): void {
    const  customer = {
        name: formData.customer.name,
        contactNo: formData.customer.contactNo,
        email: formData.customer.email,
        gstNo: formData.customer.gstNo,
        panNo: formData.customer.panNo
    }
    
    this.customerService.updateCustomer(customerId, customer)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSaving = false;
          this.toastr.success('Customer updated successfully!', 'Success');
          this.router.navigate(['/customers']);
        },
        error: (error) => {
          this.isSaving = false;
          console.error('Error updating customer:', error);
          this.toastr.error('Failed to update customer', 'Error');
        }
      });
  }

  private transformFormDataToAPI(formData: CustomerFormData): any {
    const sites = formData.sites.map(site => {
      const region = formData.regions.find(region => region.id === site.region_id);
      return {
        name: site.name,
        regionName: region ? region.name : '',
      };
    });

    return {
      customer: {
        name: formData.customer.name,
        contactNo: formData.customer.contactNo,
        email: formData.customer.email,
      },
      customerProfile: {
        name: formData.customerProfile.name,
      },
      regions: formData.regions.map(region => ({
        name: region.name,
      })),
      sites: sites,
      users: [
        ...formData.adminUsers.map(admin => ({
          username: admin.username,
          password: admin.password,
          userDetails: {
            name: admin.userDetails.name,
            email: admin.userDetails.email,
            mobile: admin.userDetails.mobile
          },
          roleNames: ['Admin'],
          regionNames: admin.regionNames,
          siteNames: []
        })),
        ...formData.operatorUsers.map(operator => ({
          username: operator.username,
          password: operator.password,
          userDetails: {
            name: operator.userDetails.name,
            email: operator.userDetails.email,
            mobile: operator.userDetails.mobile
          },
          roleNames: ['Operator'],
          regionNames: operator.regionNames,
          siteNames: operator.siteNames
        }))
      ]
    };
  }

  onFormCancel(): void {
    this.router.navigate(['/customers']);
  }

  onStepChange(stepIndex: number): void {
    console.log('Step changed to:', stepIndex);
  }

  onValidationChange(isValid: boolean): void {
    console.log('Form validation changed:', isValid);
  }

  getPageTitle(): string {
    return this.editMode ? 'Edit Customer' : 'Create New Customer';
  }

  getPageDescription(): string {
    return this.editMode 
      ? 'Update customer information, regions, sites, and user assignments'
      : 'Set up a new customer with regions, sites, and user assignments';
  }
}
